import { computed, type Ref, type ComputedRef } from 'vue'
import { usePermissions } from '@/composables/usePermissions'
import { canMutateClosedSession, isSessionCloturee } from '@/utils/sessionCloture'

/**
 * Garde UI pour les ressources liées à une session clôturée.
 * - Non-admin : lecture seule
 * - Admin / SuperAdmin : mutations autorisées (audit backend)
 */
export function useClosedSessionGuard(
	session: Ref<any> | ComputedRef<any> | (() => any)
) {
	const { isAdmin, isSuperAdmin } = usePermissions()

	const resolvedSession = computed(() => {
		if (typeof session === 'function') return session()
		return session.value
	})

	const isClosed = computed(() => isSessionCloturee(resolvedSession.value))

	const canMutate = computed(() => {
		if (!isClosed.value) return true
		return canMutateClosedSession(isAdmin.value, isSuperAdmin.value)
	})

	const isReadOnlyArchive = computed(() => isClosed.value && !canMutate.value)

	const archiveHint = computed(() => {
		if (!isClosed.value) return ''
		if (canMutate.value) {
			return 'Session clôturée — modification sur archive (action auditée).'
		}
		return 'Session clôturée — consultation uniquement.'
	})

	return {
		isClosed,
		canMutate,
		isReadOnlyArchive,
		archiveHint,
	}
}
